import { useNavigate } from '@tanstack/react-router';
import { Eye, LogOut } from 'lucide-react';
import { useSession } from '../stores/session';

/**
 * Global read-only impersonation banner (admin "Open as"). Pinned to the bottom
 * so it's always visible while an admin views another account read-only. "Exit"
 * restores the admin session and returns to where it was opened. Renders nothing
 * when not impersonating.
 */
export function ImpersonationBanner() {
  const impersonation = useSession((s) => s.impersonation);
  const exitImpersonation = useSession((s) => s.exitImpersonation);
  const navigate = useNavigate();
  if (!impersonation) return null;

  function exit() {
    const returnTo = impersonation!.returnTo;
    exitImpersonation();
    void navigate({ to: returnTo });
  }

  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] flex justify-center px-3 pb-3 pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-amber-300/70 dark:border-amber-500/40 bg-amber-50/95 dark:bg-amber-950/90 backdrop-blur px-4 py-2 shadow-lg">
        <span className="grid place-items-center w-6 h-6 rounded-full bg-amber-200 dark:bg-amber-800/70 text-amber-800 dark:text-amber-200 flex-shrink-0">
          <Eye className="w-3.5 h-3.5" />
        </span>
        <span className="text-sm text-amber-900 dark:text-amber-100">
          Viewing as <strong className="font-semibold">{impersonation.targetName}</strong>
          <span className="hidden sm:inline"> — read-only</span>
        </span>
        <button
          type="button"
          onClick={exit}
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-900 text-amber-50 dark:bg-amber-100 dark:text-amber-950 px-3 py-1 text-xs font-semibold hover:opacity-90 transition"
        >
          <LogOut className="w-3.5 h-3.5" />
          Exit
        </button>
      </div>
    </div>
  );
}
